import { MAX_INTRO_ITEMS } from '@/utils/constants'

/**
 * Pure helpers for collection intro items ("item in description"): 0–2 item
 * copies rendered beside the collection description. The array always comes
 * from the collection payload's `introItems`; old cached payloads may lack the
 * field entirely, so every helper is null-safe.
 */

/** Free intro slots left on a collection (never negative). */
export function remainingIntroSlots(introItems) {
  return Math.max(0, MAX_INTRO_ITEMS - (introItems?.length ?? 0))
}

/** Whether another intro item can still be placed. */
export function hasIntroSlotFree(introItems) {
  return remainingIntroSlots(introItems) > 0
}

/**
 * Intro items in render order: placement (createdAt) ascending, capped at
 * MAX_INTRO_ITEMS — a restore from Deleted can briefly push the stored count
 * over the cap, in which case only the first two render.
 */
export function visibleIntroItems(introItems) {
  return [...(introItems ?? [])]
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    .slice(0, MAX_INTRO_ITEMS)
}

/** The intro block renders when there is a description OR any intro item. */
export function showIntroBlock(description, introItems) {
  return !!description || visibleIntroItems(introItems).length > 0
}

/** Focus-layout picker route for placing intro items into a collection. */
export function introItemsPickPath(collectionId) {
  return `/collections/${collectionId}/intro-items/pick`
}

/**
 * Picker selection entries vs the collection's saved intro items. An entry
 * carrying `introItemId` represents a saved intro item (checked via its
 * `copiedFrom` source id); an entry without one is a new addition.
 *
 * Re-links additions to an unlinked saved intro item with the same source
 * (`copiedFrom`): unchecking a saved item and re-checking its source must
 * return to "no change", not turn into a remove + fresh copy.
 */
export function reconcileIntroSelection(entries, savedIntroItems) {
  const linkedIntroIds = new Set(entries.map(entry => entry.introItemId).filter(Boolean))

  return entries.map(entry => {
    if (entry.introItemId) return entry
    const saved = (savedIntroItems ?? []).find(item =>
      item.copiedFrom === entry.itemId && !linkedIntroIds.has(item._id))
    if (!saved) return entry
    linkedIntroIds.add(saved._id)
    return { ...entry, collectionId: null, introItemId: saved._id }
  })
}

/**
 * The send diff: additions = entries not linked to a saved intro item,
 * removedIntroIds = saved intro items whose link is gone from the selection.
 */
export function introSelectionDiff(entries, savedIntroItems) {
  const keptIds = entries.map(entry => entry.introItemId).filter(Boolean)

  return {
    additions: entries.filter(entry => !entry.introItemId),
    removedIntroIds: (savedIntroItems ?? []).map(item => item._id).filter(id => !keptIds.includes(id)),
  }
}
